import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Award, Users, Clock, ShieldCheck } from "lucide-react";

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Award,
      title: "Doświadczenie",
      description: "Ponad 20 lat szkolenia kierowców",
    },
    {
      icon: Users,
      title: "Indywidualne podejście",
      description: "Każdy kursant traktowany indywidualnie",
    },
    {
      icon: Clock,
      title: "Elastyczność",
      description: "Dopasowane terminy jazd",
    },
    {
      icon: ShieldCheck,
      title: "Bezpieczeństwo",
      description: "Nowe, bezpieczne samochody",
    },
  ];

  return (
    <section
      id="o-nas"
      className="py-20 lg:py-32 bg-white"
      data-testid="about-section"
      ref={ref}
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1634653516598-18047146cb1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Instruktor szkoły jazdy"
                className="w-full h-[400px] lg:h-[500px] object-cover rounded-sm"
                data-testid="about-image"
              />
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-yellow-400 -z-10" />
              <div className="absolute -top-4 -left-4 w-20 h-20 border-4 border-slate-900 -z-10" />
            </div>
            
            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="absolute bottom-8 right-8 bg-slate-900 text-white p-6 rounded-sm shadow-xl max-w-xs"
              data-testid="about-floating-card"
            >
              <div className="font-heading font-bold text-yellow-400 text-4xl mb-2">20+</div>
              <div className="text-sm text-slate-300">lat doświadczenia w szkoleniu kierowców</div>
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Section Label */}
            <div className="inline-block bg-yellow-400/20 text-slate-900 px-4 py-2 rounded-sm font-heading font-bold text-sm tracking-wider mb-6">
              O NAS
            </div>

            <h2
              className="font-heading font-bold text-4xl lg:text-5xl text-slate-900 tracking-tight mb-6"
              data-testid="about-title"
            >
              FRANCISZEK SMRECZAK
              <span className="block text-yellow-500">OŚRODEK SZKOLENIA KIEROWCÓW</span>
            </h2>

            <p className="text-slate-600 text-lg leading-relaxed mb-8" data-testid="about-description">
              Nasza szkoła jazdy to gwarancja profesjonalnego przygotowania do egzaminu państwowego. 
              Franciszek Smreczak z pasją i cierpliwością szkoli przyszłych kierowców już od ponad 20 lat. 
              Stawiamy na spokojną atmosferę nauki i indywidualne podejście do każdego kursanta.
            </p>

            <p className="text-slate-600 leading-relaxed mb-10">
              Nasi kursanci zdają egzaminy w Małopolskim Ośrodku Ruchu Drogowego w <strong>Nowym Targu</strong>. 
              Doskonale znamy tamtejsze trasy egzaminacyjne i skutecznie przygotowujemy do jazdy 
              w każdych warunkach.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-6" data-testid="about-features">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 bg-yellow-400 rounded-sm flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-slate-900" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-slate-900 text-sm tracking-wide">
                      {feature.title}
                    </div>
                    <div className="text-slate-500 text-sm mt-1">{feature.description}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
